import { format } from "date-fns/format";
import { nb } from "date-fns/locale/nb";
import { VehicleCancellation } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleCancellation/VehicleCancellation";
import { VehicleUndoCancellation } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleCancellation/VehicleUndoCancellation";
import { VehicleReplacement } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleReplacement/VehicleReplacement";
import { isShuttleVehicle } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/utils";
import { VehicleSummary } from "features/CenterContent/VehicleDetails/shared/VehicleSummary";
import { vehicleTypeToIcon } from "features/CenterContent/VehicleDetails/shared/utils";
import { removeStationFromStopName } from "features/VehicleSidebar/VehicleList/utils/stopNameUtils";
import { FC } from "react";
import { HStack, Text, VStack } from "shared/components/Spor";
import {
  AlternativeTransportItineraryStop,
  AlternativeTransportVehicle,
  StopReference,
} from "shared/types/alternativeTransport";
import { useTheme } from "styled-components";

type Props = {
  alternativeTransport: AlternativeTransportVehicle;
};

export const Itinerary: FC<Props> = ({ alternativeTransport }) => {
  const {
    lineNames,
    tripPatternId,
    trainIds,
    itinerary,
    type,
    vehicleStatus,
    driverPhoneNumber,
    hastusShuttlePlanId,
    originStopName,
    destinationStopName,
    firstStopName,
    scheduledDepartureTimeOrigin,
    scheduledArrivalTimeDestination,
    logs,
    operatorName,
  } = alternativeTransport;

  // Prefix unused variables with an underscore so the linter accepts them as intentionally unused
  const [_Icon, _iconTitle] = vehicleTypeToIcon.get(type)!;

  const isShuttle = isShuttleVehicle(alternativeTransport);
  const stops = itinerary?.stops ?? [];
  const returnStops = itinerary?.returnStops ?? [];
  const theme = useTheme();

  const distributionStopReferences: Array<StopReference> = [
    ...stops,
    ...returnStops,
  ].map((value: AlternativeTransportItineraryStop) => value.stopReference);

  const turnaroundStopName =
    returnStops.length > 0 ? returnStops[returnStops.length - 1].name : null;

  const replacesTrainIds = trainIds.length > 0 ? trainIds.join(", ") : null;

  const tripDetails = [lineNames, hastusShuttlePlanId || tripPatternId]
    .filter((stop): stop is string => stop !== null && stop !== "")
    .join(" / ");

  const stations = [
    removeStationFromStopName(originStopName),
    removeStationFromStopName(destinationStopName),
    turnaroundStopName,
  ]
    .filter((stop): stop is string => stop !== null && stop !== "")
    .join("–");

  const timeInterval = [
    scheduledDepartureTimeOrigin,
    scheduledArrivalTimeDestination,
  ]
    .filter((dateTime): dateTime is Date => dateTime !== null)
    .map((dateTime) => format(dateTime, "HH:mm (dd.MM.yy)", { locale: nb }))
    .join("–");

  return (
    <HStack alignItems="stretch" mt="-8px">
      <VehicleSummary
        title={replacesTrainIds ?? _iconTitle}
        type={type}
        subTitle={tripDetails}
        route={stations.concat(
          isShuttle && firstStopName && firstStopName !== originStopName
            ? ` (oppstart ${firstStopName})`
            : "",
        )}
        interval={timeInterval}
        vehicleStatus={vehicleStatus}
        logs={logs}
        driverPhoneNumber={driverPhoneNumber}
      />
      <VStack
        minW="max-content"
        align="end"
        gap={1}
        mt="6px"
        justify="space-between"
        flex={1}
      >
        <VStack gap={1}>
          <VehicleCancellation vehicle={alternativeTransport} />
          <VehicleUndoCancellation vehicle={alternativeTransport} />
          <VehicleReplacement
            vehicle={alternativeTransport}
            distributionStopReferences={distributionStopReferences}
          />
        </VStack>
        <Text fontStyle="italic" variant="sm" color={theme.colorTextSecondary}>
          Kjøres av: {operatorName}
        </Text>
      </VStack>
    </HStack>
  );
};
